import React, { useEffect, useState } from 'react';
import { useRoute } from 'wouter';
import {
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Image,
  Button,
  Chip,
} from '@nextui-org/react';
import { fetchTaskerProfileData, updateTaskerBlocked } from 'common/src/api/taskers';
import { Tasks } from 'common/src/utils/types';
import { BankAccount } from 'common/src/types/bank_accounts';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AvailabilityCalendar from './availability/AvailabilityCalendar';

interface Tasker {
  id: number;
  name: string;
  last_name: string;
  email: string;
  phone_number: string;
  university: string;
  career: string;
  university_year: string;
  tasks_categories: string[];
  only_tasks_online: boolean;
  own_movilization: boolean;
  certificate_regular_alumn_url: string;
  photo_selfie_url: string;
  certificate_criminal_record_url: string;
  brand: string;
  model: string;
  patent: string;
  offered_taskers: number;
  availability: number[][]; // Updated type
  rut: string;
  is_approved: boolean;
  profile_completed: boolean;
  application_status: string;
  city: string;
  experience?: string;
  is_blocked: boolean;
}

interface TaskerCategory {
  id: number;
  category_id: number;
  details: {
    experience_description?: string;
    [key: string]: unknown;
  };
  is_completed: boolean;
  categories: {
    name: string;
  };
}

interface TaskReview {
  id: number;
  score: number;
  comment: string;
}

interface ExperienceDetails {
  [key: string]: string | string[] | unknown;
}

const renderExperience = (details: ExperienceDetails | null | undefined) => {
  if (!details) return 'No hay información disponible';

  if (typeof details === 'string') return details;

  if (typeof details === 'object') {
    return (
      <div className="whitespace-pre-wrap font-mono text-sm">
        {Object.entries(details).map(([key, value]) => (
          <div key={key} className="mb-2">
            <span className="font-semibold">{key}:</span>
            <br />
            {Array.isArray(value) ? (
              value.map((item, index) => (
                <span key={index} className="ml-4 block">
                  • {item}
                </span>
              ))
            ) : (
              <span className="ml-4 block">{String(value)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return JSON.stringify(details, null, 2);
};

const TaskerProfilePage: React.FC = () => {
  const [match, params] = useRoute<{ id: string }>('/tasker/:id');
  const [tasker, setTasker] = useState<Tasker | null>(null);
  const [taskerCategories, setTaskerCategories] = useState<TaskerCategory[]>([]);
  const [taskReviews, setTaskReviews] = useState<TaskReview[]>([]);
  const [taskerTasks, setTaskerTasks] = useState<Tasks[]>([]);
  const [taskerBankAccount, setTaskerBankAccount] = useState<BankAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBlocking, setIsBlocking] = useState(false);

  useEffect(() => {
    const fetchTaskerData = async () => {
      if (!params?.id) return;

      try {
        const { tasker, categories, reviews, tasks, bankAccount } = await fetchTaskerProfileData(
          params.id
        );
        setTasker(tasker);
        setTaskerCategories(categories);
        setTaskReviews(reviews);
        setTaskerTasks(tasks);
        setTaskerBankAccount(bankAccount);
      } catch (error) {
        console.error('Error fetching tasker data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskerData();
  }, [params?.id]);

  const toggleBlockStatus = async () => {
    if (!tasker) return;

    setIsBlocking(true);
    try {
      const { data, error } = await updateTaskerBlocked(tasker.id, !tasker.is_blocked);
      if (error) throw error;

      setTasker({ ...tasker, is_blocked: data.is_blocked });
    } catch (error) {
      console.error('Error toggling block status:', error);
    } finally {
      setIsBlocking(false);
    }
  };

  const copyBankAccount = async () => {
    if (taskerBankAccount === null) return;
    const parsedText = Object.entries({
      ['Banco']: taskerBankAccount.bank,
      ['Tipo de cuenta']: taskerBankAccount.account_type,
      ['Número de cuenta']: taskerBankAccount.account_number,
      ['Nombre de titular']: taskerBankAccount.name,
      ['Rut de titular']: taskerBankAccount.rut,
    })
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(parsedText);
      toast.success('Datos bancarios copiados', {
        hideProgressBar: true,
        position: 'top-center',
      });
    } catch (error) {
      console.error(error);
      toast.error('No fue posible copiar los datos bancarios', {
        hideProgressBar: true,
        position: 'bottom-center',
      });
    }
  };

  if (!match) {
    return <div className="p-4">Error: Ruta no encontrada</div>;
  }

  if (loading) {
    return <Spinner label="Cargando..." />;
  }

  if (!tasker) {
    return <div className="p-4">Error: No se encontró el Tasker</div>;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Perfil del Tasker</h1>
        <div>
          <Button
            color={tasker.is_blocked ? 'success' : 'danger'}
            onClick={toggleBlockStatus}
            isLoading={isBlocking}
          >
            {tasker.is_blocked ? 'Desbloquear Cuenta' : 'Bloquear Cuenta'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Información principal del Tasker */}
        <Card className="col-span-1">
          <CardBody>
            <div className="flex flex-col items-center">
              <Image
                src={tasker.photo_selfie_url || 'https://via.placeholder.com/150'}
                alt={`${tasker.name} ${tasker.last_name}`}
                width={80}
                height={80}
                className="rounded-full object-cover mb-2"
              />
              <h2 className="text-xl font-bold mb-1">{`${tasker.name} ${tasker.last_name}`}</h2>
              <p className="text-sm text-gray-500 mb-2">{tasker.city}</p>
              <Button size="sm" className="mb-2">
                Llamar
              </Button>
              <div className="w-full flex justify-between items-center text-sm">
                <div className="text-center">
                  <p className="font-bold">{taskerCategories.length}</p>
                  <p className="text-xs text-gray-500">Categorías</p>
                </div>
                <div className="text-center">
                  <p className="font-bold">
                    {taskReviews.length > 0
                      ? (
                          taskReviews.reduce((acc, review) => acc + review.score, 0) /
                          taskReviews.length
                        ).toFixed(1)
                      : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">Rating</p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Resumen del Perfil */}
        <Card className="col-span-1">
          <CardHeader>
            <h3 className="text-lg font-semibold">Resumen del Perfil</h3>
          </CardHeader>
          <CardBody>
            <div className="text-sm">
              <p>
                <strong>Email:</strong> {tasker.email}
              </p>
              <p>
                <strong>Teléfono:</strong> {tasker.phone_number}
              </p>
              <p>
                <strong>Universidad:</strong> {tasker.university}
              </p>
              <p>
                <strong>Carrera:</strong> {tasker.career}
              </p>
              <p>
                <strong>Año universitario:</strong> {tasker.university_year}
              </p>
              <p>
                <strong>RUT:</strong> {tasker.rut}
              </p>
              <p>
                <strong>Estado:</strong>{' '}
                <Chip size="sm" color={tasker.is_approved ? 'success' : 'warning'}>
                  {tasker.is_approved ? 'Aprobado' : 'Pendiente'}
                </Chip>
              </p>
              <p>
                <strong>Perfil completado:</strong>{' '}
                <Chip size="sm" color={tasker.profile_completed ? 'success' : 'warning'}>
                  {tasker.profile_completed ? 'Sí' : 'No'}
                </Chip>
              </p>
              <p>
                <strong>Solo tareas online:</strong> {tasker.only_tasks_online ? 'Sí' : 'No'}
              </p>
              <p>
                <strong>Movilización propia:</strong> {tasker.own_movilization ? 'Sí' : 'No'}
              </p>
            </div>
          </CardBody>
        </Card>

        {/* Datos bancarios del Tasker */}
        <Card className="col-span-1">
          <CardHeader className="flex items-center">
            <h3 className="text-lg font-semibold mr-1">Datos bancarios</h3>
            <Button
              aria-label="Copiar datos bancarios"
              isIconOnly
              onPress={copyBankAccount}
              variant="light"
            >
              <Icon icon="solar:clipboard-linear" width={24} />
            </Button>
          </CardHeader>
          <CardBody>
            <div className="text-sm">
              {taskerBankAccount ? (
                <>
                  <p>
                    <span className="font-bold">Banco:</span> {taskerBankAccount.bank}
                  </p>
                  <p>
                    <span className="font-bold">Tipo de cuenta:</span>{' '}
                    {taskerBankAccount.account_type}
                  </p>
                  <p>
                    <span className="font-bold">Número de cuenta:</span>{' '}
                    {taskerBankAccount.account_number}
                  </p>
                  <p>
                    <span className="font-bold">Nombre de titular:</span> {taskerBankAccount.name}
                  </p>
                  <p>
                    <span className="font-bold">Rut de titular:</span> {taskerBankAccount.rut}
                  </p>
                </>
              ) : (
                <p>No hay detalles de cuenta disponibles.</p>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Certificados y Categorías */}
        <div className="col-span-3 grid grid-cols-3 gap-4">
          {/* Certificados y Categorías */}
          <Card className="col-span-1">
            <CardHeader>
              <h3 className="text-lg font-semibold">Certificados y Categorías</h3>
            </CardHeader>
            <CardBody>
              <div className="flex flex-col space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Certificados:</h4>
                  <div className="flex flex-col space-y-2">
                    <Button
                      size="sm"
                      color="primary"
                      as="a"
                      href={tasker.certificate_regular_alumn_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs"
                    >
                      Certificado de Alumno Regular
                    </Button>
                    <Button
                      size="sm"
                      color="primary"
                      as="a"
                      href={tasker.certificate_criminal_record_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs"
                    >
                      Certificado de Antecedentes
                    </Button>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Categorías:</h4>
                  <div className="flex flex-wrap gap-1">
                    {taskerCategories.map((category) => (
                      <Chip key={category.id} size="sm" color="primary">
                        {category.categories.name}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Disponibilidad del Tasker */}
          <Card className="col-span-2">
            <CardHeader>
              <h3 className="text-lg font-semibold">Disponibilidad del Tasker</h3>
            </CardHeader>
            <CardBody>
              {tasker.availability ? (
                <AvailabilityCalendar availability={tasker.availability as number[][]} />
              ) : (
                <p>No hay información de disponibilidad.</p>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Experiencia */}
        <Card className="col-span-3">
          <CardHeader>
            <h3 className="text-lg font-semibold">Experiencia por Categoría</h3>
          </CardHeader>
          <CardBody>
            <Table aria-label="Experiencia por Categoría">
              <TableHeader>
                <TableColumn>Categoría</TableColumn>
                <TableColumn>Experiencia</TableColumn>
              </TableHeader>
              <TableBody>
                {taskerCategories.map((category, index) => (
                  <TableRow key={category.id} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <TableCell>{category.categories.name}</TableCell>
                    <TableCell>{renderExperience(category.details)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        {/* Tareas del Tasker */}
        <Card className="col-span-3">
          <CardHeader>
            <h3 className="text-lg font-semibold">Tasks del Tasker</h3>
          </CardHeader>
          <CardBody>
            <Table>
              <TableHeader>
                <TableColumn>ID</TableColumn>
                <TableColumn>Categoría</TableColumn>
                <TableColumn>Descripción</TableColumn>
                <TableColumn>Estado</TableColumn>
                <TableColumn>Precio</TableColumn>
                <TableColumn>Método de Pago</TableColumn>
                <TableColumn>Fecha</TableColumn>
              </TableHeader>
              <TableBody>
                {taskerTasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.id}</TableCell>
                    <TableCell>{task.category}</TableCell>
                    <TableCell>{task.description}</TableCell>
                    <TableCell>
                      <Chip color={getStatusColor(task.status)}>{task.status}</Chip>
                    </TableCell>
                    <TableCell>${task.price}</TableCell>
                    <TableCell>{task.payment_method || 'N/A'}</TableCell>
                    <TableCell>{new Date(task.created_at).toLocaleDateString('es-CL')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        {/* Reseñas */}
        <Card className="col-span-3">
          <CardHeader>
            <h3 className="text-lg font-semibold">Reseñas</h3>
          </CardHeader>
          <CardBody>
            <Table>
              <TableHeader>
                <TableColumn>Puntuación</TableColumn>
                <TableColumn>Comentario</TableColumn>
              </TableHeader>
              <TableBody>
                {taskReviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell>{review.score}</TableCell>
                    <TableCell>{review.comment}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'in_progress':
      return 'primary';
    case 'completed':
      return 'success';
    default:
      return 'default';
  }
};

export default TaskerProfilePage;
